import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ServerIcon,
  ArrowPathIcon,
  TableCellsIcon,
  HashtagIcon,
  ClockIcon,
  ChartBarIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { useUI } from '../../contexts/UIContext';
import { useBilling } from '../../contexts/BillingContext';
import { metadataAPI } from '../../api/enhancedApiService';
import { apiRequest } from '../../utils/apiUtils';
import { useTablesData } from '../../hooks/useTablesData';
import { useMetadataStatus } from '../../hooks/useMetadataStatus';
import { prefetchCriticalPath, invalidateQueriesSelective } from '../../api/queryClient';
import { transformTablesForHierarchy, getUniqueSchemas, filterTablesBySchema } from '../../utils/schemaHierarchy';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import HierarchicalDataExplorer from '../../components/common/HierarchicalDataExplorer';

const DataExplorerPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { connections, activeConnection, setCurrentConnection } = useConnection();
  const { updateBreadcrumbs, showNotification, setLoading } = useUI();
  const { isFeatureEnabled, plan } = useBilling();

  // Check if tier 4 (profiling) is enabled for this plan
  const enabledTiers = plan?.features?.enabled_tiers || [1, 2];
  const tier4Enabled = enabledTiers.includes(4) || isFeatureEnabled('tier4_enabled');

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSchema, setSelectedSchema] = useState('');
  const [isRunningProfiling, setIsRunningProfiling] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({
    connection: true,
    'schema-public': true
  });
  const [activeTab, setActiveTab] = useState('tables');
  

  // Use hooks for data fetching (tiered metadata)
  const {
    data: tablesData = { tables: [], tierStatus: null, count: 0 },
    isLoading: isLoadingTables,
    refetch: refetchTables
  } = useTablesData(
    activeConnection?.id,
    { enabled: !!activeConnection?.id, includeTierStatus: true }
  );

  const tables = tablesData.tables || [];
  const tierStatus = tablesData.tierStatus;

  const { data: metadataStatus = {}, refetch: refetchMetadataStatus } = useMetadataStatus(
    activeConnection?.id,
    { enabled: !!activeConnection?.id }
  );

  // Load connection from URL if provided and prefetch critical data
  useEffect(() => {
    const connectionId = searchParams.get('connection');
    const tab = searchParams.get('tab') || 'tables';
    const subTab = searchParams.get('subtab') || 'overview';
    
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
    
    
    if (connectionId && connections.length > 0) {
      const connection = connections.find(c => c.id === connectionId);
      if (connection && (!activeConnection || activeConnection.id !== connectionId)) {
        setCurrentConnection(connection);
        // Prefetch critical data for better UX
        prefetchCriticalPath(connectionId, {
          includeValidations: true,
          includeMetadata: true
        }).catch(console.warn);
      }
    }
  }, [searchParams, connections, activeConnection, setCurrentConnection]);

  // Set breadcrumbs
  useEffect(() => {
    const breadcrumbs = [
      { name: 'Data Explorer', href: '/explorer' }
    ];
    updateBreadcrumbs(breadcrumbs);
  }, [updateBreadcrumbs]);

  // Update UI loading state based on data fetching
  useEffect(() => {
    setLoading('explorer', isLoadingTables);
  }, [isLoadingTables, setLoading]);


  // Handle tab change
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Memoized handlers to prevent unnecessary rerenders
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const handleTableSelect = useCallback((tableName) => {
    if (!activeConnection) {
      showNotification('Please select a connection first', 'warning');
      return;
    }
    navigate(`/explorer/${activeConnection.slug}/tables/${encodeURIComponent(tableName)}`);
  }, [activeConnection, navigate, showNotification]);

  const toggleNodeExpanded = useCallback((nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  }, []);

  // Handle metadata refresh
  const handleRefreshMetadata = useCallback(async () => {
    if (!activeConnection) {
      showNotification('Please select a connection first', 'warning');
      return;
    }

    try {
      setLoading('refreshMetadata', true);
      await metadataAPI.refreshMetadata(activeConnection.id, 'schema');
      showNotification('Metadata refresh initiated', 'success');

      // Use selective invalidation instead of broad refetch
      setTimeout(() => {
        invalidateQueriesSelective(['metadata'], { 
          predicate: (queryKey) => queryKey.includes(activeConnection.id) 
        });
        invalidateQueriesSelective(['tables'], { 
          predicate: (queryKey) => queryKey.includes(activeConnection.id) 
        });
        setLoading('refreshMetadata', false);
      }, 2000);
    } catch (error) {
      console.error('Error refreshing metadata:', error);
      showNotification('Failed to refresh metadata', 'error');
      setLoading('refreshMetadata', false);
    }
  }, [activeConnection, showNotification, setLoading, refetchTables, refetchMetadataStatus]);

  // Handle data profiling
  const handleRunDataProfiling = useCallback(async () => {
    if (!activeConnection) {
      showNotification('Please select a connection first', 'warning');
      return;
    }

    try {
      setIsRunningProfiling(true);
      
      const response = await apiRequest(`/automation/trigger-immediate/${activeConnection.id}`, {
        method: 'POST',
        data: {
          automation_type: 'table_profiling'
        }
      });

      if (response.success) {
        showNotification('Data profiling started successfully. You can monitor progress in the Automation page.', 'success');
      } else {
        throw new Error(response.error || 'Failed to start data profiling');
      }
    } catch (error) {
      console.error('Error starting data profiling:', error);
      showNotification(error.message || 'Failed to start data profiling', 'error');
    } finally {
      setIsRunningProfiling(false);
    }
  }, [activeConnection, showNotification]);

  // Format metadata freshness for display
  const getMetadataFreshness = () => {
    // NEW: Use tier status for metadata freshness
    if (metadataStatus?.tierStatus) {
      const { isStale, lastExecution } = metadataStatus.tierStatus;

      const statusColors = {
        fresh: 'text-accent-500',
        stale: 'text-warning-500',
        unknown: 'text-secondary-400'
      };

      if (lastExecution) {
        const status = isStale ? 'stale' : 'fresh';
        return (
          <span className={statusColors[status]}>
            {isStale ? 'Needs refresh' : 'Up to date'}
          </span>
        );
      }
    }

    // LEGACY: Check old metadata status format (backward compatibility)
    const tablesMeta = metadataStatus?.tables?.freshness ||
                      metadataStatus?.freshness ||
                      metadataStatus?.status;

    // If no metadata status available, check if we have tables loaded
    if (!tablesMeta) {
      if (tables && tables.length > 0) {
        return 'Loaded';
      }
      return 'Unknown';
    }

    const statusColors = {
      fresh: 'text-accent-500',
      stale: 'text-warning-500',
      unknown: 'text-secondary-400',
      error: 'text-danger-500',
      loaded: 'text-accent-500'
    };

    // Handle string status
    if (typeof tablesMeta === 'string') {
      return (
        <span className={statusColors[tablesMeta] || statusColors.unknown}>
          {tablesMeta === 'fresh' || tablesMeta === 'loaded' ? 'Up to date' :
           tablesMeta === 'stale' ? 'Needs refresh' :
           tablesMeta === 'error' ? 'Error' : tablesMeta}
        </span>
      );
    }

    // Handle object status
    const status = tablesMeta?.status || 'unknown';
    return (
      <span className={statusColors[status] || statusColors.unknown}>
        {status === 'fresh' ? 'Up to date' :
         status === 'stale' ? 'Needs refresh' :
         status === 'error' ? 'Error' : 'Unknown'}
      </span>
    );
  };

  return (
    <div className="py-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold text-secondary-900">
          Data Explorer
        </h1>

        {activeConnection && (
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefreshMetadata}
              disabled={isLoadingTables}
              className="inline-flex items-center px-3 py-1.5 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {isLoadingTables ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Refreshing...
                </>
              ) : (
                <>
                  <ArrowPathIcon className="mr-2 h-4 w-4 text-secondary-500" />
                  Refresh Metadata
                </>
              )}
            </button>
            
            {tier4Enabled ? (
              <button
                onClick={handleRunDataProfiling}
                disabled={isRunningProfiling || isLoadingTables}
                className="inline-flex items-center px-3 py-1.5 border border-primary-300 shadow-sm text-sm font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isRunningProfiling ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Running...
                  </>
                ) : (
                  <>
                    <ChartBarIcon className="mr-2 h-4 w-4 text-primary-500" />
                    Run Data Profiling
                  </>
                )}
              </button>
            ) : (
              <Link
                to="/settings/billing"
                className="inline-flex items-center px-3 py-1.5 border border-amber-300 shadow-sm text-sm font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                title="Upgrade to Starter plan or higher to enable data profiling"
              >
                <LockClosedIcon className="mr-2 h-4 w-4 text-amber-500" />
                Data Profiling (Upgrade)
              </Link>
            )}
          </div>
        )}
      </div>

      {activeConnection ? (
        <div className="mt-2 space-y-3">
          <div className="flex items-center">
            <ServerIcon className="h-5 w-5 text-secondary-500 mr-2" />
            <span className="font-medium text-secondary-700">{activeConnection.name}</span>
          </div>

          {/* Tier Status Indicator */}
          {tierStatus && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">
                  Metadata Collection Status
                </div>
                <div className="text-xs text-gray-500">
                  {tables.length} tables | {tierStatus.coverage?.tables_with_schema || 0} with schema | {tierStatus.coverage?.tables_with_profiling || 0} profiled
                </div>
              </div>
              {/* Tier badge indicators showing T1-T5 status */}
              <div className="mt-2 flex items-center gap-2">
                {[
                  { tier: 1, name: 'Discovery', status: tierStatus.tier1?.last_job_status },
                  { tier: 2, name: 'Schema', status: tierStatus.tier2?.last_job_status },
                  { tier: 3, name: 'Changes', status: tierStatus.tier3?.last_job_status },
                  { tier: 4, name: 'Profiling', status: tierStatus.tier4?.last_job_status },
                  { tier: 5, name: 'Usage', status: tierStatus.tier5?.last_job_status }
                ].map(({ tier, name, status }) => {
                  const colorClass = status === 'completed' ? 'bg-green-500' :
                                    status === 'running' ? 'bg-blue-500 animate-pulse' :
                                    status === 'failed' ? 'bg-red-500' : 'bg-gray-300';
                  return (
                    <div key={tier} className="flex items-center gap-1">
                      <div className={`w-2 h-2 rounded-full ${colorClass}`} title={`${name}: ${status || 'not run'}`} />
                      <span className="text-xs text-gray-600">T{tier}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-2 p-4 bg-warning-50 border border-warning-200 rounded-md">
          <p className="text-warning-700">Please select a connection to view data.</p>
        </div>
      )}

      {activeConnection ? (
        <div className="mt-6">
          {/* Main Tabs Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => handleTabChange('tables')}
                className={`${
                  activeTab === 'tables'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <TableCellsIcon className="w-5 h-5 mr-2" />
                Tables & Schema
              </button>
            </nav>
          </div>
          

          {/* Tab Content */}
          {activeTab === 'tables' && (
            <>
              {/* Connection Stats Bar */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-secondary-200 p-4">
              <div className="flex items-center">
                <TableCellsIcon className="h-8 w-8 text-primary-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-secondary-600">Tables</p>
                  <p className="text-2xl font-semibold text-secondary-900">{tables.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border border-secondary-200 p-4">
              <div className="flex items-center">
                <ServerIcon className="h-8 w-8 text-accent-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-secondary-600">Connection</p>
                  <p className="text-lg font-semibold text-secondary-900">
                    {activeConnection?.db_type || activeConnection?.database_type || activeConnection?.type || 'Database'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-secondary-200 p-4">
              <div className="flex items-center">
                <ClockIcon className="h-8 w-8 text-secondary-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-secondary-600">Metadata</p>
                  <p className="text-lg font-semibold text-secondary-900">
                    {getMetadataFreshness() || 'Unknown'}
                  </p>
                </div>
              </div>
            </div>
          </div>

              {/* Schema-First Table Explorer */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-secondary-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-medium text-secondary-900">Explore Tables</h3>

                    {/* Schema Filter */}
                    <div className="flex items-center space-x-3">
                      <label htmlFor="schema-filter" className="text-sm font-medium text-secondary-700">
                        Filter by Schema:
                      </label>
                      <select
                        id="schema-filter"
                        value={selectedSchema}
                        onChange={(e) => setSelectedSchema(e.target.value)}
                        className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                      >
                        <option value="">All Schemas</option>
                        {getUniqueSchemas(tables).map(schema => (
                          <option key={schema} value={schema}>{schema}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-secondary-500">
                    Navigate by database → schema → table hierarchy
                  </p>
                </div>
            <div className="p-6">
              {isLoadingTables ? (
                <div className="flex justify-center items-center h-64">
                  <LoadingSpinner size="lg" />
                  <div className="ml-3">
                    <p className="text-secondary-600">Loading tables...</p>
                  </div>
                </div>
              ) : (
                <HierarchicalDataExplorer
                  data={transformTablesForHierarchy(
                    selectedSchema ? filterTablesBySchema(tables, selectedSchema) : tables,
                    activeConnection
                  )}
                  onItemSelect={(item) => handleTableSelect(item.full_name || item.object_name)}
                  selectedItem={null}
                  title="Data Objects"
                  showCounts={true}
                  renderItemBadges={(item) => {
                    // Calculate tier based on metadata flags
                    let tier = 1; // Default: discovered

                    if (item.has_profiling || item.is_profiled) {
                      tier = 4; // Has profiling data
                    } else if (item.has_schema_metadata) {
                      tier = 2; // Has schema metadata
                    }

                    // Only show badge if table has metadata
                    if (tier > 1) {
                      const tierColors = {
                        2: 'bg-green-100 text-green-700',
                        4: 'bg-purple-100 text-purple-700'
                      };

                      return (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${tierColors[tier]}`}>
                          T{tier}
                        </span>
                      );
                    }
                    return null;
                  }}
                />
              )}
            </div>
          </div>
            </>
          )}
          
        </div>
      ) : (
        <div className="mt-6">
          <EmptyState
            icon={ServerIcon}
            title="No Connection Selected"
            description="Please select a connection to view tables and schema."
            actionText="View Connections"
            actionLink="/connections"
          />
        </div>
      )}
    </div>
  );
};

export default DataExplorerPage;